import React, { useEffect } from 'react';
import { Link, Redirect, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import Loader from '@cpg-its/ads.common.loading-circle';
import { useDispatch, useSelector } from 'react-redux';
import { Grid, Hidden } from '@material-ui/core';
import { RadioFilter } from '../../../../search/components/radio-filter';
import { SearchApp } from '../../../../search/components/search-app';
import { SearchList } from '../../../../search/components/search-list';
import { CleryAndLay, EmploymentTyes } from '../../../../search/constant/static-lists';
import { SearchProps } from '../../models/arc-epi-props';
import { Employee } from '../employment/employment-model';
import { InstitutionState } from '../institutions/institution-model';
import { EmployeeItemView } from './employee-item-view';
import { EmployeeListLabels } from './employee-list-labels';
import { SearchBar } from '../../../../search/components/search-bar';
import { FilterGroup } from '../../../../search/components/filter-group';
import { ListPagination } from '../../../../search/components/list-pagination';
import { PageHeader } from '../../../../global/components/layout/page-header';
import { TcsmStates } from '../../../../root/tcsm-model';
import { useBasePath } from '../../../../global/hooks/useBasePath';
import { ArcRoutes } from '../../constants/routes';
import { dispatchSetCurrentEmployee, dispatchSetEmployeeList, setCurrentEmployee, unSetEmploymentState } from '../employment/employment-slice';
import { unloadCompensationState } from '../compensation/compensation-history/compensation-history-slice';
import { unloadPersonState } from '../../../account-info/features/personal-info/personal-info-slice';
import { DemographicAppKeys } from '../../../../global/models/demographics-model';
import { unloadRelationshipState } from '../../../account-info/features/relationships/relationships-slice';
import { unloadDependentState } from '../../../account-info/features/dependents/dependents-slice';
import { searchPageStyles } from '../../../../global/constants/theme/styles/pages/search/search-page-layout';
import { employeeSearchTableStyles } from '../../../../global/constants/theme/styles/tables/employee-search-table';
import { ContentSpacer } from '../../../../global/components/layout/generic-content-spacer';
import { SideBarContent } from '../../../../global/components/layout/sidebar-content';
import { GuidedProcessSupportBlock } from '../../../../global/components/guided-process-support-block';
import { useGpsType } from '../../../../global/hooks/useGpsType';
import { GpsType } from '../../../../global/components/layout/layout-models';
import { paddingStyles } from '../../../../global/constants/theme/styles/layout/paddingStyles';
import { MainContent } from '../../../../global/components/layout/main-content-container';
import { buttonStyles } from '../../../../global/constants/theme/styles/components/buttonstyles';
import { ButtonWrapper } from '../../../../global/components/layout/button-wrapper';
import { setEmployeeContext, setExternalLinkState, setNavigationContext } from '../../../../global/ducks/app-context';
import { replaceAll } from '../../../../global/helpers/string-helpers';
import { EmployeeContext, NavigationContext } from '../../../../global/models/app-context-model';
import { getEmployeesList } from '../../../../global/ducks/benadmin-api';
import { setEmployeeListLoaded } from '../institutions/institution-slice';
import { setIsReceivingPensionOrPayout } from '../../../account-info/features/personal-info/document-upload-slice';

export interface Props {
	defaultOnEmployeeSelectRedirectUrl: string,
}

function EmployeeSearch(props: Props) {

	const institutionState = useSelector<TcsmStates, InstitutionState>(state => state.institutionState);
	const initialList = useSelector<TcsmStates, Employee[]>(state => state.employmentState.employeeList);
	const searchSettings = useSelector<TcsmStates, SearchProps>(state => state.epiProps.searchSettingsState.epiContent);
	const institutionSelect = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.InstitutionRoot}${ArcRoutes.InstitutionSearch}`);
	const institutionId = institutionState?.currentInstitution?.org_guid;
	const nonStipNewHireRoute = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.NonStipNewHire}`);
	const stipNewHireRoute = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.StipNewHire}`);
	const terminatePage = useBasePath(`${ArcRoutes.ArcRoot}${ArcRoutes.PeopleRoot}${ArcRoutes.EmploymentTermination}`);

	const { state } = useLocation<any>();
	const dispatch = useDispatch();
	const history = useHistory();
	const routeMatch = useRouteMatch();

    const pageClasses = searchPageStyles();
    const buttonClasses = buttonStyles();
    const tableClasses = employeeSearchTableStyles();
    const gpsItem = useGpsType(GpsType.EMPLOYEE_LIST);
    const paddingClasses = paddingStyles();

	useEffect(() => {
		dispatch(unSetEmploymentState());
		dispatch(unloadCompensationState());
		dispatch(unloadPersonState(DemographicAppKeys.arc));
		dispatch(unloadRelationshipState(DemographicAppKeys.arc));
		dispatch(unloadDependentState(DemographicAppKeys.arc));
		dispatch(setIsReceivingPensionOrPayout(null, DemographicAppKeys.arc));
	}, []);

	useEffect(() => {
		if (institutionId) {
			dispatch(getEmployeesList(institutionId, (response) => {
				dispatchSetEmployeeList(dispatch)(response);
				dispatch(setEmployeeListLoaded());
			}));
		}
	}, [institutionId]);

	if (!institutionState?.institutionClientNumber) {
		return (
			<Redirect to=
				{
					{
						pathname: institutionSelect,
						state: {
							actionLink: routeMatch.path,
							finalAction: state?.actionLink,
							externalLinkAction: state?.externalLinkAction,
						},
					}
				}
			/>);
	}

	const onSetEmployee = (employeeGuid: string) => {
		if (state?.externalLinkAction) {
			dispatch(setExternalLinkState({
				renderModal: true,
				linkUrl: replaceAll(state.externalLinkAction, '{personGuid}', employeeGuid),
				linkInnerHtml: '',
			}));
		}

		const match = initialList.filter(x => x.guid === employeeGuid)[0];
		if (match) {
			dispatchSetCurrentEmployee(dispatch, match);
			dispatch(setEmployeeContext(EmployeeContext.InstitutionEmployee));
			dispatch(setNavigationContext(NavigationContext.Institution));
		}

		let redirectPage = state?.finalAction || state?.actionLink;

		if (!redirectPage || routeMatch.path === redirectPage) {
			redirectPage = props.defaultOnEmployeeSelectRedirectUrl;
		}

		history.push(redirectPage);
	};

	const onRemoveEmployee = (employeeGuid: string) => {
		const match = initialList.filter(x => x.guid === employeeGuid)[0];
		if (match) {
			dispatchSetCurrentEmployee(dispatch, match);
			dispatch(setCurrentEmployee(match as Employee));
		}
		history.push(terminatePage);
	};

	if (!institutionState.employeeListLoaded) {
		return (<Loader />);
	}

	return (
		<>
			<Grid className={paddingClasses.containerBottomPadding} container>
				<MainContent>
					<PageHeader
						headerText={searchSettings?.employeeSearchPageTitle}
						description={searchSettings?.employeeSearchPageDescription}
					/>


					<ButtonWrapper>
						<Link className={buttonClasses.secondaryButton} to={stipNewHireRoute}>{searchSettings?.addEmployeeLinkText}</Link>
						<Link className={buttonClasses.secondaryButton} to={nonStipNewHireRoute}>{searchSettings?.addNonStipMemberLinkText}</Link>
					</ButtonWrapper>

				</MainContent>
				<SideBarContent>
					<GuidedProcessSupportBlock title={gpsItem?.title} contents={gpsItem?.contents} />
				</SideBarContent>
			</Grid>
			<SearchApp initialList={initialList} appName={`${institutionState?.institutionClientNumber}-EmployeeSearch-${initialList?.length}`}>

				<Grid container alignItems='flex-start'>
					<Hidden lgUp>
						<Grid container>
							<Grid item xs={12} md={9} lg={6}>
								<SearchBar label={searchSettings.searchBarLabel} name='employee-search-bar' filterProps={['fullAddress', 'full_name', 'masked_client_number']} />
							</Grid>
						</Grid>
					</Hidden>

					<FilterGroup hide={initialList?.length < 10} >
						<Grid className={pageClasses.searchFilters} container item xs={12} lg={3}>
							<Grid item xs={12}>
								<ContentSpacer>
									<h2>{searchSettings?.filterSectionTitle}</h2>
								</ContentSpacer>
							</Grid>
							<Grid className='filters-group' item xs={12} sm={4} lg={12}>
								<RadioFilter
									name='employmentType'
									defaultValue={{
										filterKey: 'employmentType',
										filterDisplayValue: 'All Employment Types',
										filterValue: '',
									}}
									options={EmploymentTyes}
									label={searchSettings.employmentTypeLabel}
								/>
							</Grid>
							{
								/*  Disable for now
							<Grid className='filters-group' item xs={12} sm={4} lg={12}>
								<RadioFilter
									name='status_indicator'
									defaultValue={{
										filterKey: 'status_indicator',
										filterDisplayValue: 'Complete And Missing Info',
										filterValue: '',
									}}
									options={InformationStatus}
									label={searchSettings.informationLabel}
								/>
							</Grid>
								*/
							}
							<Grid className='filters-group' item xs={12} sm={4} lg={12}>
								<RadioFilter
									name='status'
									defaultValue={{
										filterKey: 'status',
										filterDisplayValue: 'Both',
										filterValue: '',
									}}
									options={CleryAndLay}
									label={searchSettings.cleryLayLabel}
								/>
							</Grid>
						</Grid>
					</FilterGroup>

					<Grid item xs>
						<Hidden mdDown>
							<Grid container>
								<Grid item xs={12} md={9} lg={6}>
									<SearchBar label={searchSettings.searchBarLabel} name='employee-search-bar' filterProps={['fullAddress', 'full_name', 'masked_client_number']} />
								</Grid>
							</Grid>
						</Hidden>
						<div className={tableClasses.searchTable}>
							<Hidden xsDown>
								<EmployeeListLabels settings={searchSettings} />
							</Hidden>
							<div className='employee-search-table-content'>
								<SearchList
									renderList={
										(item) =>
											<EmployeeItemView
												key={item.id}
												item={item}
												settings={searchSettings}
												onSetEmployee={() => onSetEmployee((item as Employee)?.guid)}
												onRemoveClick={() => onRemoveEmployee((item as Employee)?.guid)}
											/>

									}
									renderPagination={
										(totalCounts, name) => {
											return <ListPagination name={name} totalCounts={totalCounts} totalPages={Math.ceil(totalCounts / 10)} nextButton={searchSettings.nextButton} prevButton={searchSettings.previousButton} />;
										}
									}
									emptyMessage={searchSettings.emptyListMessage}
								/>
							</div>
						</div>
					</Grid>

				</Grid>
			</SearchApp>
		</>
	);
}

export default EmployeeSearch;